import { useId, type ReactNode, type Ref } from 'react';
import { Library, Loader2, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileSourceChooser } from '@/features/file-library';
import type { FileSourceMode } from '@/features/file-library/types';
import { cn } from '@/lib/utils';

interface TruckPhotoDirectionZoneProps {
  title: string;
  statusLabel: string;
  hasContent: boolean;
  optional?: boolean;
  uploading?: boolean;
  canUpload?: boolean;
  uploadLabel?: string;
  inputLabel?: string;
  accept?: string;
  multiple?: boolean;
  error?: string | null;
  /** Focus anchor for server conflict remediation (spec-13 §8.7): binds the
   * real upload input so a failed confirm can move focus to the exact
   * offending control. */
  uploadInputRef?: Ref<HTMLInputElement>;
  /** Fallback focus anchor when no upload control renders (read-only). */
  sectionRef?: Ref<HTMLElement>;
  children?: ReactNode;
  sourceMode?: FileSourceMode;
  canUseLibrary?: boolean;
  libraryLabel?: string;
  librarySelectedLabel?: string | null;
  onSourceModeChange?: (mode: FileSourceMode) => void;
  onLibrarySelectClick?: () => void;
  onFilesSelected?: (files: FileList) => void;
}

/**
 * Direction is encoded by the upload destination rather than a detached
 * select. This keeps the UI aligned with the left/right/rear confirmation
 * invariant and prevents a file from inheriting a stale direction choice.
 */
export const TruckPhotoDirectionZone = ({
  title,
  statusLabel,
  hasContent,
  optional = false,
  uploading = false,
  canUpload = false,
  uploadLabel,
  inputLabel,
  accept,
  multiple = false,
  error,
  uploadInputRef,
  sectionRef,
  children,
  sourceMode = 'local',
  canUseLibrary = false,
  libraryLabel,
  librarySelectedLabel,
  onSourceModeChange,
  onLibrarySelectClick,
  onFilesSelected,
}: TruckPhotoDirectionZoneProps) => {
  const id = useId();
  const titleId = `${id}-title`;
  const errorId = `${id}-error`;

  return (
    <section
      role="group"
      ref={sectionRef}
      tabIndex={-1}
      aria-labelledby={titleId}
      aria-describedby={error ? errorId : undefined}
      className={cn(
        'flex min-h-40 min-w-0 flex-col gap-3 rounded-md border bg-efs-bg-panel p-3',
        error
          ? 'border-red-500'
          : hasContent
            ? 'border-emerald-500/60'
            : optional
              ? 'border-efs-border'
              : 'border-amber-500/60',
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h3 id={titleId} className="min-w-0 text-sm font-semibold text-efs-text-primary">
          {title}
        </h3>
        <Badge
          variant="outline"
          aria-live="polite"
          className={cn(
            'shrink-0',
            error
              ? 'border-red-500 text-red-900 dark:text-red-100'
              : hasContent
                ? 'border-emerald-500 text-emerald-900 dark:text-emerald-100'
                : optional
                  ? 'border-efs-border text-efs-text-muted'
                  : 'border-amber-500 text-amber-900 dark:text-amber-100',
          )}
        >
          {statusLabel}
        </Badge>
      </div>

      <div className="min-h-0 min-w-0 flex-1">{children}</div>

      {canUpload && canUseLibrary && onSourceModeChange ? (
        <FileSourceChooser
          value={sourceMode}
          onChange={onSourceModeChange}
          disabled={uploading}
          className="w-full"
        />
      ) : null}

      {canUpload &&
      canUseLibrary &&
      sourceMode === 'library' &&
      onLibrarySelectClick &&
      libraryLabel ? (
        <Button
          type="button"
          variant="outline"
          className="min-h-11 w-full min-w-0 justify-center gap-2"
          disabled={uploading}
          onClick={onLibrarySelectClick}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Library className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="min-w-0 break-words">{libraryLabel}</span>
        </Button>
      ) : null}

      {librarySelectedLabel ? (
        <p className="break-all rounded-md bg-efs-bg-subtle px-2 py-1 text-xs text-efs-text-muted">
          {librarySelectedLabel}
        </p>
      ) : null}

      {canUpload && sourceMode === 'local' && uploadLabel && inputLabel && onFilesSelected ? (
        <label
          className={cn(
            'flex min-h-11 min-w-0 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-efs-border bg-efs-bg-subtle px-3 py-2 text-center text-sm font-medium transition-colors',
            'hover:border-efs-accent hover:text-efs-accent focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
            uploading && 'pointer-events-none opacity-60',
          )}
        >
          <input
            type="file"
            ref={uploadInputRef}
            accept={accept}
            multiple={multiple}
            disabled={uploading}
            aria-label={inputLabel}
            className="sr-only"
            onChange={(event) => {
              if (event.target.files?.length) onFilesSelected(event.target.files);
              event.target.value = '';
            }}
          />
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Upload className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="min-w-0 break-words">{uploadLabel}</span>
        </label>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="text-xs text-red-900 dark:text-red-100">
          {error}
        </p>
      ) : null}
    </section>
  );
};
